import { RootState } from "@base/store";
import Button from "@components/atoms/button";
import Divider from "@components/atoms/divider";
import { gameDetailsActions } from "@components/features/game-details/gameDetails.slice";
import {
  Box,
  ClickAwayListener,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { ReactElement, useEffect, useState } from "react";
import { ConnectedProps, connect, useSelector } from "react-redux";

import {
  CardActionWrapper,
  CardTitle,
  Container,
  Content,
  CurrencyWrapper,
  DateWrapper,
  DetailFooterWrapper,
  EditButton,
  FeesAndCurrencyWrapper,
  Footer,
  FooterInfo,
  FooterWrapper,
  GameDetailTypography,
  Header,
  IconWrapper,
  Info,
  InfoContainer,
  InfoIconWrapper,
  InfoWrapper,
  Rating,
  RatingMessage,
  RatingWrapper,
  ResultWrapper,
  Score,
  ScoreTextField,
  ScoreWrapper,
  TitleWrapper,
  UnRatedMessage,
  ViewScoreWrapper,
  Wrapper,
} from "./organizerFinshedGameDetailsCard.styled";
import { ComponentProps } from "./organizerFinshedGameDetailsCard.type";

import { ReactComponent as Calendar } from "@assets/images/calender/calendar.svg";
import { ReactComponent as Cash } from "@assets/images/cash/BsCash.svg";
import { ReactComponent as Chat } from "@assets/images/chat/chat-bubble-left-ellipsis.svg";
import { ReactComponent as ChevronDown } from "@assets/images/chevrons/chevron-down.svg";
import { ReactComponent as ChevronUp } from "@assets/images/chevrons/chevron-up.svg";
import { ReactComponent as USD } from "@assets/images/currencies/BsCurrencyDollar.svg";
import { ReactComponent as EUR } from "@assets/images/currencies/BsCurrencyEuro.svg";
import { ReactComponent as GBP } from "@assets/images/currencies/BsCurrencyPound.svg";
import { ReactComponent as AED } from "@assets/images/currencies/trade 1.svg";
import { ReactComponent as InfoIcon } from "@assets/images/info/CkInfoOutline.svg";
import { ReactComponent as PinMap } from "@assets/images/pin-map/map-pin.svg";
import { ReactComponent as Star } from "@assets/images/star/star.svg";
import { ReactComponent as UnActiveStar } from "@assets/images/star/un-active-star.svg";

type ReduxProps = ConnectedProps<typeof connector>;

const OrganizerFinishedGameDetailsCard = ({
  date,
  fee,
  location,
  title,
  isAttending,
  currency,
  info,
  link,
  handleEdit,
  submitScore,
  handleRSVP,
  gameRate,
  sideAId,
  sideBId,
  sideATitle,
  sideBTitle,
  scoreA,
  scoreB,
}: ComponentProps & ReduxProps) => {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.extend(localizedFormat);
  dayjs.extend(customParseFormat);
  const [isExpanded, setExpanded] = useState<boolean>(false);
  const [sideAResult, setSideAResult] = useState(scoreA || 0);
  const [sideBResult, setSideBResult] = useState(scoreB || 0);
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const [open, setOpen] = useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };
  const selectedGame = useSelector((state: RootState) => state.selectedGame);

  const currencyRender = (currency: string) => {
    switch (currency) {
      case "USD":
        return <USD width={15} height={15} />;
      case "AED":
        return <AED width={15} height={15} />;
      case "GBP":
        return <GBP width={15} height={15} />;
      case "EUR":
        return <EUR width={15} height={15} />;
      default:
        break;
    }
  };

  const handleSideAChange = (event: any) => {
    const inputValue = event.target.value;

    if (/^\d{0,2}$/.test(inputValue)) {
      setSideAResult(inputValue);
    }
  };

  const handleSideBChange = (event: any) => {
    const inputValue = event.target.value;

    if (/^\d{0,2}$/.test(inputValue)) {
      setSideBResult(inputValue);
    }
  };

  const InfoRender = (icon: ReactElement, title?: string, isDate?: boolean) => {
    const date = isDate && title?.split(",");

    return (
      <Info>
        <Box>{icon}</Box>
        <>
          {isDate ? (
            <DateWrapper>
              <Typography>{`${date && date[0]}`}</Typography>
              <Typography>{`${date && date[1]}`}</Typography>
            </DateWrapper>
          ) : (
            <Typography>{title}</Typography>
          )}
        </>
      </Info>
    );
  };

  const CurrencyPriceRender = (icon: ReactElement, title: string | number) => {
    return (
      <Info>
        <Box>{icon}</Box>
        <CurrencyWrapper isPound={currency === "GBP"}>
          <Typography sx={{ wordWrap: "break-word" }}>{title}</Typography>
          {currencyRender(currency)}
        </CurrencyWrapper>
      </Info>
    );
  };

  function shortenUrlToLength(originalUrl: string, length: number) {
    if (originalUrl.length <= length) {
      return originalUrl;
    }

    // Extract the protocol and domain
    const url = new URL(originalUrl);
    const protocolDomain = url.protocol + "//" + url.host;

    // Calculate the available space for the path and query
    const availableSpace = length - protocolDomain.length - 3; // 3 for "..."

    // Get the path and query
    let pathQuery = url.pathname + url.search;

    // Shorten the path and query if necessary
    if (pathQuery.length > availableSpace) {
      pathQuery = pathQuery.slice(0, availableSpace) + "...";
    }

    // Reconstruct the shortened URL
    const shortenedUrl = protocolDomain + pathQuery;

    return shortenedUrl;
  }

  const FooterInfoRender = (
    icon: ReactElement,
    title: string | number,
    isLink: boolean
  ) => {
    return (
      <FooterInfo>
        <Box>{icon}</Box>
        {isLink ? (
          <Link
            sx={{ wordWrap: "break-word", cursor: "pointer" }}
            onClick={() => {
              window.open(link, "_blank");
            }}
          >
            {shortenUrlToLength(link!, 30)}
          </Link>
        ) : (
          <Typography
            sx={{
              wordWrap: "break-word",
              fontFamily: "NotoSans-Medium",
              fontSize: "14px",
            }}
          >
            {title}
          </Typography>
        )}
      </FooterInfo>
    );
  };

  const handleFooterClick = () => {
    setExpanded(!isExpanded);
  };

  const handleSideABlur = () => {
    !sideAResult && setSideAResult(0);
  };

  const handleSideBBlur = () => {
    !sideBResult && setSideBResult(0);
  };

  const handleSubmitScores = async () => {
    const body = {
      first_side_id: sideAId,
      second_side_id: sideBId,
      first_side_score: sideAResult,
      second_side_score: sideBResult,
    };
    const response = await submitScore(body);
    if (response?.meta?.requestStatus === "fulfilled") {
      setSideAResult(response?.payload[0]?.score_value);
      setSideBResult(response?.payload[1]?.score_value);
      setIsEditable(false);
    } else {
      setSideAResult(0);
      setSideBResult(0);
    }
  };

  const is48HoursPast = () => {
    const currentDateTime = dayjs().utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
    const endDateTimePlus48Hours = dayjs(selectedGame?.end_date)
      .utc()
      .add(48, "hours")
      .format("YYYY-MM-DDTHH:mm:ss[Z]");
    return endDateTimePlus48Hours < currentDateTime;
  };

  const cardEditRender = () => (
    <CardActionWrapper>
      <Wrapper>
        <ScoreWrapper>
          <Typography>{sideATitle}</Typography>
          <ScoreTextField
            onChange={handleSideAChange}
            onBlur={handleSideABlur}
            value={sideAResult}
            type="number"
            inputProps={{
              min: 0,
              max: 99,
              style: {
                textAlign: "center",
                fontFamily: "NotoSans-Regular",
                fontSize: "30px",
                height: "50px",
                padding: "5px 0",
              },
            }}
          />
        </ScoreWrapper>
        <Typography>-</Typography>
        <ScoreWrapper>
          <ScoreTextField
            onChange={handleSideBChange}
            onBlur={handleSideBBlur}
            value={sideBResult}
            type="number"
            inputProps={{
              min: 0,
              max: 99,
              style: {
                textAlign: "center",
                fontFamily: "NotoSans-Regular",
                fontSize: "30px",
                height: "50px",
                padding: "5px 0",
              },
            }}
          />
          <Typography>{sideBTitle}</Typography>
        </ScoreWrapper>
      </Wrapper>
      <Button
        fontFamily="Outfit-SemiBold"
        fontSize="16px"
        color="secondary"
        textColor={"#FFF"}
        text={"Submit Scores"}
        type={"button"}
        onClick={handleSubmitScores}
      />
    </CardActionWrapper>
  );

  const cardViewRender = () => (
    <CardActionWrapper>
      <Wrapper>
        <ViewScoreWrapper>
          <Typography>{sideATitle}</Typography>
          <Score>{sideAResult}</Score>
        </ViewScoreWrapper>
        <Typography>-</Typography>
        <ViewScoreWrapper>
          <Score>{sideBResult}</Score>
          <Typography>{sideBTitle}</Typography>
        </ViewScoreWrapper>
      </Wrapper>
      {!is48HoursPast() && (
        <EditButton onClick={() => setIsEditable(true)}>Edit Score</EditButton>
      )}
    </CardActionWrapper>
  );

  useEffect(() => {
    if (!scoreA && !scoreB) {
      setIsEditable(true);
    }
  }, [scoreA, scoreB]);

  return (
    <Container>
      <Header>
        <TitleWrapper>
          <CardTitle>{title}</CardTitle>
        </TitleWrapper>
        <InfoIconWrapper>
          {handleEdit && (
            <ClickAwayListener onClickAway={handleTooltipClose}>
              <Tooltip
                onClick={handleTooltipOpen}
                PopperProps={{
                  disablePortal: true,
                }}
                onClose={handleTooltipClose}
                open={open}
                disableFocusListener
                disableHoverListener
                disableTouchListener
                placement="bottom-end"
                arrow
                title="Editing is available within 48hrs after the end of the game."
              >
                <InfoIcon />
              </Tooltip>
            </ClickAwayListener>
          )}
        </InfoIconWrapper>
      </Header>
      <Content>
        <ResultWrapper>
          {isEditable ? cardEditRender() : cardViewRender()}
        </ResultWrapper>
        <>
          {gameRate && gameRate?.user_rating ? (
            <RatingWrapper>
              <Star />
              {gameRate.average_rating && (
                <Rating>{`Game Rating ${gameRate.average_rating}.0`}</Rating>
              )}
              {gameRate.user_rating && (
                <RatingMessage>{`(You rated this game ${gameRate.user_rating}/5)`}</RatingMessage>
              )}
            </RatingWrapper>
          ) : (
            <RatingWrapper>
              <UnActiveStar />
              <UnRatedMessage>{`You haven't rated this game yet`}</UnRatedMessage>
            </RatingWrapper>
          )}
        </>
        <Footer onClick={handleFooterClick}>
          {isExpanded ? (
            <>
              <DetailFooterWrapper>
                <InfoContainer>
                  {InfoRender(<Calendar />, date, true)}
                  <FeesAndCurrencyWrapper>
                    {CurrencyPriceRender(<Cash />, fee.toString())}
                  </FeesAndCurrencyWrapper>
                  {InfoRender(<PinMap />, location)}
                </InfoContainer>
                <InfoWrapper>
                  {link && FooterInfoRender(<Chat />, link, true)}
                  {info && <Typography>{info}</Typography>}
                </InfoWrapper>
                <br />
                <IconWrapper>
                  <ChevronUp />
                </IconWrapper>
              </DetailFooterWrapper>
            </>
          ) : (
            <>
              <Divider orientation="horizontal" />
              <FooterWrapper onClick={handleFooterClick}>
                <GameDetailTypography>{"Game details"}</GameDetailTypography>
                <IconWrapper>
                  <ChevronDown />
                </IconWrapper>
              </FooterWrapper>
            </>
          )}
        </Footer>
      </Content>
    </Container>
  );
};

const mapDispatchToProps = {
  submitScore: gameDetailsActions.createScore,
  rsvpToAGame: gameDetailsActions.rsvpToAGame,
  reset: gameDetailsActions.reset,
};

const connector = connect(undefined, mapDispatchToProps);
const OrganizerFinishedGameDetailsCardRedux = connector(
  OrganizerFinishedGameDetailsCard
);

export { OrganizerFinishedGameDetailsCardRedux as OrganizerFinishedGameDetailsCard };
